import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../api/api';
import { TrendingUp, Users, DollarSign, Target, Calendar, ArrowUpRight, BarChart3, Award, RefreshCw, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatPct = (n) => `${(n || 0).toFixed(1)}%`;

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function MenuEstadisticas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearsDisp, setYearsDisp] = useState([]);
  const [sortBy, setSortBy] = useState('confirmados');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMonths, setSelectedMonths] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      let url = `${API_URL}/api/menus/estadisticas?year=${selectedYear}`;
      if (selectedMonths.length > 0) {
        url += `&months=${selectedMonths.join(',')}`;
      }
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      setData(d);
      if (d.years_disponibles?.length) setYearsDisp(d.years_disponibles);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedYear, selectedMonths]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleMonth = (mes) => {
    setSelectedMonths(prev =>
      prev.includes(mes) ? prev.filter(m => m !== mes) : [...prev, mes].sort((a, b) => a - b)
    );
  };

  const clearMonths = () => setSelectedMonths([]);

  const sortedMenus = data?.menus ? [...data.menus].sort((a, b) => {
    const va = a[sortBy] || 0, vb = b[sortBy] || 0;
    return sortDir === 'desc' ? vb - va : va - vb;
  }) : [];

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-slate-800 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortBy === field && <span className="text-[10px]">{sortDir === 'desc' ? '\u25BC' : '\u25B2'}</span>}
      </span>
    </th>
  );

  const maxMensual = data ? Math.max(...data.tendencia_mensual.map(m => m.ventas), 1) : 1;

  if (loading && !data) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      <span className="ml-3 text-slate-500">Cargando estadisticas...</span>
    </div>
  );

  if (!data) return <div className="text-center py-20 text-slate-400">No se pudieron cargar las estadisticas</div>;

  const r = data.resumen;

  return (
    <div className="space-y-6" data-testid="menu-estadisticas">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Estadisticas de Ventas</h2>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select
            data-testid="stats-year-selector"
            value={selectedYear}
            onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setSelectedMonths([]); }}
            className="text-sm font-medium text-slate-700 bg-transparent border-none outline-none cursor-pointer"
          >
            {yearsDisp.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stats-kpis">
        <KpiCard icon={<DollarSign className="w-4 h-4" />} label="Ingresos" value={formatCurrency(r.total_ingresos)} color="emerald" />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Beneficio" value={formatCurrency(r.total_beneficio)} color="blue" />
        <KpiCard icon={<BarChart3 className="w-4 h-4" />} label="Margen" value={formatPct(r.margen_medio)} color="violet" />
        <KpiCard icon={<Award className="w-4 h-4" />} label="Confirmados" value={r.total_eventos_confirmados} color="amber" />
        <KpiCard icon={<Target className="w-4 h-4" />} label="Presupuestados" value={r.total_presupuestados} color="slate" />
        <KpiCard icon={<Users className="w-4 h-4" />} label="Personas" value={r.total_personas_servidas?.toLocaleString('es-ES')} color="indigo" />
        <KpiCard icon={<ArrowUpRight className="w-4 h-4" />} label="Conversion" value={formatPct(r.tasa_conversion_global)} color="teal" />
      </div>

      {/* Tendencia mensual - clickable */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="tendencia-mensual">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Tendencia Mensual {selectedYear}
            {selectedMonths.length > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-400">
                (filtrado: {selectedMonths.map(m => MONTH_NAMES[m - 1]).join(', ')})
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            {selectedMonths.length > 0 && (
              <button
                onClick={clearMonths}
                data-testid="clear-month-filter"
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
              >
                <X className="w-3 h-3" />
                Limpiar filtro
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mb-2">Haz clic en las barras para filtrar por mes</p>
        <div className="flex items-end gap-1.5" style={{ minHeight: '200px' }}>
          {data.tendencia_mensual.map(m => {
            const pct = maxMensual > 0 ? (m.ventas / maxMensual) * 100 : 0;
            const currentMonth = new Date().getMonth() + 1;
            const isCurrent = m.mes === currentMonth && selectedYear === new Date().getFullYear();
            const isSelected = selectedMonths.includes(m.mes);
            const hasFilter = selectedMonths.length > 0;
            const topMenu = m.top_menus?.[0];

            let barColor;
            if (isSelected) {
              barColor = 'bg-emerald-500';
            } else if (hasFilter) {
              barColor = m.ventas > 0 ? 'bg-slate-300' : 'bg-slate-100';
            } else if (isCurrent) {
              barColor = 'bg-emerald-500';
            } else {
              barColor = m.ventas > 0 ? 'bg-slate-700' : 'bg-slate-200';
            }

            return (
              <div
                key={m.mes}
                className="flex-1 flex flex-col items-center gap-1 group relative cursor-pointer"
                onClick={() => m.ventas > 0 && toggleMonth(m.mes)}
                data-testid={`menu-month-bar-${m.mes}`}
              >
                {/* Tooltip */}
                {m.top_menus?.length > 0 && (
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-52 bg-slate-800 text-white rounded-lg p-2.5 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wide mb-1.5">Top {m.nombre_mes} {selectedYear}</p>
                    {m.top_menus.map((tm, i) => (
                      <div key={i} className="flex items-center gap-1.5 py-0.5">
                        <span className={`text-[10px] font-bold w-4 text-center ${i === 0 ? 'text-amber-400' : 'text-slate-400'}`}>{i + 1}</span>
                        <span className="text-[10px] text-slate-200 truncate flex-1">{tm.nombre}</span>
                        <span className="text-[10px] font-semibold text-slate-300">{tm.ventas}x</span>
                        <span className="text-[9px] text-slate-400">{tm.personas}p</span>
                      </div>
                    ))}
                    <div className="text-[9px] text-slate-400 mt-1.5 pt-1.5 border-t border-slate-700">
                      {m.ventas} eventos · {m.personas} personas · {formatCurrency(m.ingresos)}
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
                  </div>
                )}
                <div className="w-full flex flex-col items-center justify-end h-28">
                  <div
                    className={`w-full max-w-[40px] rounded-t-md transition-all ${barColor} ${isSelected ? 'ring-2 ring-emerald-300 ring-offset-1' : ''}`}
                    style={{ height: `${Math.max(pct > 0 ? 6 : 2, pct)}%` }}
                  />
                </div>
                <span className={`text-[10px] ${isSelected ? 'font-bold text-emerald-600' : isCurrent ? 'font-bold text-emerald-600' : 'text-slate-400'}`}>{m.nombre_mes}</span>
                {topMenu ? (
                  <span className="text-[9px] text-slate-500 font-medium truncate w-full text-center px-0.5" title={topMenu.nombre}>
                    {topMenu.nombre.length > 12 ? topMenu.nombre.substring(0, 12) + '...' : topMenu.nombre}
                  </span>
                ) : m.ventas > 0 ? (
                  <span className="text-[9px] text-slate-400">{m.ventas}ev</span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabla ranking */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ranking-menus">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Ranking de Menus
            {selectedMonths.length > 0 && (
              <span className="ml-2 text-xs font-normal text-emerald-500">
                {selectedMonths.map(m => MONTH_NAMES[m - 1]).join(' + ')}
              </span>
            )}
          </h3>
          {selectedMonths.length > 0 && (
            <span className="text-xs text-slate-400">{sortedMenus.length} menus</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-8">#</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Menu</th>
                <SortHeader field="confirmados">Vendidos</SortHeader>
                <SortHeader field="presupuestados">Presup.</SortHeader>
                <SortHeader field="tasa_conversion">Conv.</SortHeader>
                <SortHeader field="personas_confirmadas">Uds</SortHeader>
                <SortHeader field="personas_media_evento">Media</SortHeader>
                <SortHeader field="precio_medio_venta">Precio</SortHeader>
                <SortHeader field="ingresos_confirmados">Ingresos</SortHeader>
                <SortHeader field="coste_rentabilidad_pp">Coste</SortHeader>
                <SortHeader field="margen_porcentaje">Margen</SortHeader>
                <SortHeader field="beneficio_total">Beneficio</SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sortedMenus.map((m, i) => {
                const rank = i + 1;
                return (
                  <tr key={m.menu_id} className="hover:bg-slate-50 transition-colors" data-testid={`menu-row-${rank}`}>
                    <td className="px-3 py-2.5 text-slate-400 font-mono text-xs">{rank}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-[180px]">
                        <span className="font-medium text-slate-800 truncate max-w-[220px]" title={m.nombre}>{m.nombre}</span>
                        {m.es_coctel && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-100 text-purple-700 flex-shrink-0">COCTEL</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="font-bold text-slate-800">{m.confirmados}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center text-slate-500">{m.presupuestados}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`font-medium ${m.tasa_conversion >= 70 ? 'text-emerald-600' : m.tasa_conversion >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                        {formatPct(m.tasa_conversion)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center font-medium text-slate-700">{m.personas_confirmadas.toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2.5 text-center text-slate-500">{m.personas_media_evento}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{formatCurrency(m.precio_medio_venta)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{formatCurrency(m.ingresos_confirmados)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-500">{formatCurrency(m.coste_rentabilidad_pp)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${m.margen_porcentaje >= 60 ? 'bg-emerald-100 text-emerald-700' : m.margen_porcentaje >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {formatPct(m.margen_porcentaje)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-blue-700">{formatCurrency(m.beneficio_total)}</td>
                  </tr>
                );
              })}
            </tbody>
            {sortedMenus.length > 0 && (
              <tfoot className="bg-slate-800 text-white">
                <tr>
                  <td colSpan={2} className="px-3 py-2.5 font-bold text-sm">TOTAL</td>
                  <td className="px-3 py-2.5 text-center font-bold">{r.total_eventos_confirmados}</td>
                  <td className="px-3 py-2.5 text-center">{r.total_presupuestados}</td>
                  <td className="px-3 py-2.5 text-center font-medium">{formatPct(r.tasa_conversion_global)}</td>
                  <td className="px-3 py-2.5 text-center font-bold">{r.total_personas_servidas?.toLocaleString('es-ES')}</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(r.total_ingresos)}</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-center font-bold">{formatPct(r.margen_medio)}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(r.total_beneficio)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {sortedMenus.length === 0 && (
          <div className="p-8 text-center text-slate-400">No hay datos de menus para {selectedYear}{selectedMonths.length > 0 ? ` (${selectedMonths.map(m => MONTH_NAMES[m - 1]).join(', ')})` : ''}</div>
        )}
      </div>

      {/* Nota */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-500">
        <strong>Nota:</strong> Haz clic en las barras del grafico para filtrar por mes (multi-seleccion).
        La rentabilidad se calcula excluyendo costes de personal excepto para menus tipo coctel, donde el personal es parte integral del servicio.
        Coste = ingredientes + materiales. Coctel = ingredientes + materiales + personal.
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, color }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.slate}`} data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 mb-1 opacity-75">{icon}<span className="text-[10px] font-semibold uppercase tracking-wide">{label}</span></div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
